import { collection, onSnapshot } from "firebase/firestore";
import { GetListParams, GetListResult } from "react-admin";
import { applyFilter, applyPagination, applySort } from "../backoffice.utils";
import { firestore } from "../firebase";
import type { TheoryQuestion } from "../model/TheoryQuestion";
import { getTheoryQuestionsCatalog } from "../model/TheoryQuestionsCatalog";
import { AbstractDataProvider } from "./AbstractDataProvider";

export interface TheoryQuestionRecord {
  id: string;
  typ: "Multiple-Choice-Frage" | "Zahlenfrage" | "Bildauswahlfrage";
  questionText: string;
  hintRating?: number;
  media: "" | "image" | "video";
  imageUrl?: string;
}

let _hintsPromise: undefined | Promise<Map</* theory question id: */ string, /* hint: */ string>>;
let _hintRatingsPromise: undefined | Promise<Map</* `<theory question id>:<hint>`: */ string, number>>;

class TheoryQuestionsProvider extends AbstractDataProvider<TheoryQuestionRecord> {
  async getList(
    _resource: string,
    { filter, sort, pagination }: GetListParams,
  ): Promise<GetListResult<TheoryQuestionRecord>> {
    const [catalog, hints, hintRatings] = await Promise.all([
      getTheoryQuestionsCatalog(),
      (() => {
        if (!_hintsPromise) {
          _hintsPromise = new Promise((resolve) => {
            onSnapshot(collection(firestore, "theory_question_tips"), (snapshot) => {
              const currentHints = new Map<string, string>();
              for (const doc of snapshot.docs) {
                const { hint } = doc.data() as { hint: string };
                if (typeof hint === "string") {
                  currentHints.set(doc.id, hint);
                }
              }
              resolve(currentHints);
              _hintsPromise = Promise.resolve(currentHints);
            });
          });
        }
        return _hintsPromise;
      })(),
      (() => {
        if (!_hintRatingsPromise) {
          _hintRatingsPromise = new Promise((resolve) => {
            onSnapshot(collection(firestore, "theory_question_hint_ratings"), (snapshot) => {
              const hintRatings = new Map<string, number>();
              for (const doc of snapshot.docs) {
                const { hint } = doc.data() as { hint?: { [key: string]: unknown } };
                if (hint) {
                  for (const [key, value] of Object.entries(hint)) {
                    if (key !== "lastUpdateAt") {
                      const data = value as { [key: string]: unknown };
                      const hintRating = _computeHintRating(data);
                      hintRatings.set(`${doc.id}:${key}`, hintRating);
                    }
                  }
                }
              }
              resolve(hintRatings);
              _hintRatingsPromise = Promise.resolve(hintRatings);
            });
          });
        }
        return _hintRatingsPromise;
      })(),
    ]);
    const records = Object.values(catalog.questionsById).map((it) => _theoryQuestionToRecord(it, hints, hintRatings));
    return applyPagination(applySort(applyFilter(records, filter), sort), pagination);
  }
}

export const theoryQuestionsProvider = new TheoryQuestionsProvider();

function _theoryQuestionToRecord(
  question: TheoryQuestion,
  hints: Map<string, string>,
  hintRatings: Map<string, number>,
): TheoryQuestionRecord {
  const hint = hints.get(question.id);
  const hintRating = hint ? hintRatings.get(`${question.id}:${hint}`) : undefined;
  return {
    id: question.id,
    typ: question.typ,
    questionText: question.title,
    hintRating,
    media: question.video ? "video" : question.image ? "image" : "",
    imageUrl: question.video?.firstFrame?.url ?? question.image?.url,
  };
}

function _computeHintRating(data: { [key: string]: unknown }): number {
  let sum = 0;
  for (const value of Object.values(data)) {
    if (typeof value === "number") {
      sum += value;
    }
  }
  return sum;
}
